import React, { useState, useEffect } from 'react';
import ItemForm from './ItemForm';
import ItemList from './ItemList';
import ViewItemModal from './ViewItemModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import Notification from './Notification';
import Header from './Header';
import '../styles/GroceryListApp.css';

const GroceryListApp = () => {
  const [items, setItems] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [notification, setNotification] = useState(null);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    quantity: '',
    price: '',
    image: '',
    category: 'perishable'
  });

  // Load data from localStorage on mount
  useEffect(() => {
    const savedItems = localStorage.getItem('groceryItems');
    const savedDeletedIds = localStorage.getItem('deletedIds');
    
    if (savedItems) {
      setItems(JSON.parse(savedItems));
    }
    if (savedDeletedIds) {
      setDeletedIds(JSON.parse(savedDeletedIds));
    }
  }, []);

  // Save to localStorage whenever items change
  useEffect(() => {
    localStorage.setItem('groceryItems', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem('deletedIds', JSON.stringify(deletedIds));
  }, [deletedIds]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const validateForm = () => {
    // Check if ID is empty
    if (!formData.id.trim()) {
      showNotification('ID is required', 'error');
      return false;
    }

    // Check if ID is a valid integer
    const idNum = parseInt(formData.id);
    if (isNaN(idNum) || idNum.toString() !== formData.id.trim()) {
      showNotification('ID must be a valid integer', 'error');
      return false;
    }

    // Check if ID has been deleted before
    if (deletedIds.includes(idNum)) {
      showNotification(`ID ${idNum} has been deleted and cannot be reused`, 'error');
      return false;
    }

    // Check if ID already exists (only for new items, not editing)
    if (!editingId && items.some(item => item.id === idNum)) {
      showNotification('ID already exists. Please use a unique ID', 'error');
      return false;
    }

    // Check if name is empty
    if (!formData.name.trim()) {
      showNotification('Grocery name is required', 'error');
      return false;
    }

    // Check for duplicate names (optional but good UX)
    const duplicateName = items.find(item => 
      item.name.toLowerCase() === formData.name.trim().toLowerCase() && 
      item.id !== editingId
    );
    if (duplicateName) {
      showNotification('Item with this name already exists', 'error');
      return false;
    }

    // Validate quantity
    if (!formData.quantity.trim()) {
      showNotification('Quantity is required', 'error');
      return false;
    }
    const qty = parseInt(formData.quantity);
    if (isNaN(qty) || qty <= 0) {
      showNotification('Quantity must be a positive integer', 'error');
      return false;
    }

    // Validate price
    if (!formData.price.trim()) {
      showNotification('Price is required', 'error');
      return false;
    }
    const priceNum = parseFloat(formData.price);
    if (isNaN(priceNum) || priceNum <= 0) {
      showNotification('Price must be a positive number', 'error');
      return false;
    }

    // Validate image URL
    if (!formData.image.trim()) {
      showNotification('Image URL is required', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('Form submitted with data:', formData);
    
    if (!validateForm()) {
      console.log('Validation failed');
      return;
    }

    const newItem = {
      id: parseInt(formData.id),
      name: formData.name.trim(),
      quantity: parseInt(formData.quantity),
      price: parseFloat(formData.price),
      image: formData.image.trim(),
      category: formData.category,
      createdAt: editingId ? items.find(i => i.id === editingId)?.createdAt : new Date().toISOString()
    };

    console.log('New item to be added:', newItem);

    if (editingId) {
      // Update existing item
      setItems(items.map(item => item.id === editingId ? newItem : item));
      showNotification('Item updated successfully!');
      setEditingId(null);
    } else {
      // Add new item
      setItems([...items, newItem]);
      showNotification('Item added successfully!');
    }

    // Reset form
    setFormData({
      id: '',
      name: '',
      quantity: '',
      price: '',
      image: '',
      category: 'perishable'
    });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      id: item.id.toString(),
      name: item.name,
      quantity: item.quantity.toString(),
      price: item.price.toString(),
      image: item.image,
      category: item.category
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id) => {
    setItems(items.filter(item => item.id !== id));
    setDeletedIds([...deletedIds, id]);
    setShowDeleteConfirm(null);
    showNotification('Item deleted successfully!');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({
      id: '',
      name: '',
      quantity: '',
      price: '',
      image: '',
      category: 'perishable'
    });
  };

  return (
    <div className="grocery-app-container">
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      
      {/* Notification Toast */}
      {notification && <Notification notification={notification} />}

      <div className="app-content">
        {/* Header */}
        <Header />

        {/* Add/Edit Form */}
        <ItemForm 
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          editingId={editingId}
          cancelEdit={cancelEdit}
        />

        {/* Items List/Table */}
        <ItemList 
          items={items}
          handleEdit={handleEdit}
          setShowDeleteConfirm={setShowDeleteConfirm}
          setViewingItem={setViewingItem}
        />

        {/* View Item Modal/Popup */}
        {viewingItem && (
          <ViewItemModal 
            viewingItem={viewingItem}
            setViewingItem={setViewingItem}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <DeleteConfirmModal 
            showDeleteConfirm={showDeleteConfirm}
            setShowDeleteConfirm={setShowDeleteConfirm}
            handleDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
};

export default GroceryListApp;